import { DatePicker, Form, Input, Select } from 'antd'
import cls from 'classnames'
import { useAtom } from 'jotai'
import { toNumber } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, UsersSearch } from '@/components'
import { userInfoAtom } from '@/modules/user'
import { PriorityEnum } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { Customer, OverseaRegion } from '../customer.schema'
import { FieldChangedReasonFormItem } from './customer-field-change-reason.component'

const { RangePicker } = DatePicker

const Level = ['S', 'T0', 'T1+', 'T1', 'T2', 'T3', 'Default']

export const CustomerBasicForm: React.FC<{
  customer?: Customer
  admins: {
    oversea: string[]
    china: string[]
  }
  fieldsChanged: {
    startDate: boolean
    endDate: boolean
  }
}> = ({ customer, admins, fieldsChanged }) => {
  const { t } = useTranslation()

  const [user] = useAtom(userInfoAtom)

  const isAdmin =
    customer &&
    (customer?.region === OverseaRegion ? admins.oversea.includes(user.email) : admins.china.includes(user.email))
  const canEditSpecialField = !customer || isAdmin || user.isSuperAdmin

  return (
    <>
      <Form.Item label={t('customer.name')} required name='name' rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('customer.负责人')} required name='email' rules={[{ required: true }]}>
        <UsersSearch disabled={!canEditSpecialField} allowClear={false} />
      </Form.Item>
      <FieldChangedReasonFormItem field='email' customer={customer} fieldsChanged={fieldsChanged} />
      <Form.Item required label={t('customer.timePeriod')} name='range' rules={[{ required: true }]}>
        <RangePicker disabled={!canEditSpecialField} format='YYYY-MM-DD' />
      </Form.Item>
      <FieldChangedReasonFormItem field='startDate' customer={customer} fieldsChanged={fieldsChanged} />
      <FieldChangedReasonFormItem field='endDate' customer={customer} fieldsChanged={fieldsChanged} />
      <Form.Item name='region' label={t('customer.region')}>
        <Input readOnly disabled></Input>
      </Form.Item>
      {!customer ? (
        <>
          <Form.Item label={t('customer.salesVP')} required name='salesVPs' rules={[{ required: true }]}>
            <UsersSearch allowClear={false} mode='multiple' />
          </Form.Item>
          <Form.Item label={t('customer.serviceVP')} required name='serviceVPs' rules={[{ required: true }]}>
            <UsersSearch allowClear={false} mode='multiple' />
          </Form.Item>
          <Form.Item label={t('customer.salesOwner')} required name='salesOwner' rules={[{ required: true }]}>
            <UsersSearch allowClear={false} />
          </Form.Item>
          <Form.Item label={t('customer.serviceOwner')} required name='serviceOwner' rules={[{ required: true }]}>
            <UsersSearch allowClear={false} />
          </Form.Item>
          <Form.Item label={t('customer.productManager')} required name='productManager' rules={[{ required: true }]}>
            <UsersSearch allowClear={false} />
          </Form.Item>
        </>
      ) : null}
      <Form.Item label={t('customer.detail.level')} required name='level' rules={[{ required: true }]}>
        <Select
          placeholder={t('customer.detail.level')}
          options={Level.map((label) => ({
            value: label,
            label: label,
          }))}
        ></Select>
      </Form.Item>
      <Form.Item label={t('customer.detail.priority')} required name='priority' rules={[{ required: true }]}>
        <Select
          placeholder={t('customer.detail.priority')}
          disabled={!canEditSpecialField}
          options={Object.keys(PriorityEnum)
            .filter((v) => isNaN(toNumber(v)))
            .map((key) => {
              const value = PriorityEnum[key]
              return {
                value,
                label: t(`form.priority.${key}`),
              }
            })}
        ></Select>
      </Form.Item>
      <FieldChangedReasonFormItem field='priority' customer={customer} fieldsChanged={fieldsChanged} />
      <Form.Item
        label={t('customer.detail.dependencies')}
        valuePropName='content'
        trigger='onChange'
        name='dependencies'
        rules={[
          {
            required: true,
            validator: async (_, value) =>
              !removeHtmlTag(value).trim()
                ? Promise.reject(`${t('customer.detail.dependencies')}是必选填项!`)
                : Promise.resolve(),
          },
        ]}
      >
        <Editor
          disableAutoSaveStyle
          className={'editorBorder'}
          placeholder='1）对TO的依赖 2）对商务/服务 的依赖 3）对客户的依赖 4）其它依赖项'
        />
      </Form.Item>
      <Form.Item
        valuePropName='content'
        label={t('customer.detail.background')}
        name='background'
        trigger='onChange'
        rules={[
          {
            required: true,
            validator: async (_, value) =>
              !removeHtmlTag(value).trim()
                ? Promise.reject(`${t('customer.detail.background')}是必选填项!`)
                : Promise.resolve(),
          },
        ]}
      >
        <Editor className={'editorBorder'} disableAutoSaveStyle />
      </Form.Item>
      <Form.Item
        valuePropName='content'
        label={t('customer.detail.businessGoal')}
        name='businessGoal'
        trigger='onChange'
        rules={[
          {
            required: true,
            validator: async (_, value) =>
              !removeHtmlTag(value).trim()
                ? Promise.reject(`${t('customer.detail.businessGoal')}是必选填项!`)
                : Promise.resolve(),
          },
        ]}
      >
        <Editor
          editable={canEditSpecialField}
          className={cls('editorBorder', { editorDisabled: !canEditSpecialField })}
          disableAutoSaveStyle
        />
      </Form.Item>
      <FieldChangedReasonFormItem field='businessGoal' customer={customer} fieldsChanged={fieldsChanged} />
    </>
  )
}
